import { useState } from 'react'
import { ApiUser } from '@vend/types'

import {
  useRegisterManager,
  useSaleManager,
  useVdTracking,
} from '../../../../ng-compat/hooks'

import { LineItem, LineItemUser } from '../../../../models/line-item'
import { convertApiUserToLineItemUser } from '../../../../models/user'
import { useAppDispatch, useAppSelector } from '../../../../state/hooks'
import { selectLoggedInUser } from '../../../../state/slices/posState/posStateSlice'
import {
  selectSaleId,
  selectSaleLocalId,
  updateLineItem,
} from '../../../../state/slices/currentSale/currentSaleSlice'

export interface SalesPersonSelectProps {
  lineItem: LineItem
}

const getSelectedUser = (
  selectedUser: LineItemUser | null | undefined,
  loggedInUser: ApiUser | null | undefined
): LineItemUser | null => {
  if (selectedUser) {
    return selectedUser
  }

  if (loggedInUser) {
    return convertApiUserToLineItemUser(loggedInUser)
  }

  return null
}

export const useSalesPersonSelectModel = (props: SalesPersonSelectProps) => {
  const vdTracking = useVdTracking()
  const saleId = useAppSelector(selectSaleId)
  const saleLocalId = useAppSelector(selectSaleLocalId)
  const loggedInUser = useAppSelector(selectLoggedInUser)
  const dispatch = useAppDispatch()
  const saleManager = useSaleManager()
  const users = useRegisterManager().getAllSelectableUsers()
  const isDisabled = props.lineItem.confirmed || users.length === 0

  // Use logged in user if line item has no explicit sold by user
  const selectedUser: LineItemUser | null = getSelectedUser(
    props?.lineItem?.salesperson,
    loggedInUser
  )

  // Popover state
  const [isPopoverOpen, toggleIsPopoverOpen] = useState<boolean>(false)
  const showPopover = () => {
    toggleIsPopoverOpen(true)
  }
  const hidePopover = () => {
    toggleIsPopoverOpen(false)
  }

  const onSelectHandler = (user: ApiUser) => {
    vdTracking.trackUserEvent({
      eventName: 'sale.line_item.salesperson.update',
      metadata: {
        saleId: saleId || saleLocalId,
        userId: user.id,
        lineItemId: props.lineItem.id || props.lineItem.localId,
      },
    })

    const lineItemUser = convertApiUserToLineItemUser(user)
    dispatch(updateLineItem({ ...props.lineItem, salesperson: lineItemUser }))
    saleManager.saveSale()

    hidePopover()
  }

  return {
    users,
    isDisabled,
    selectedUser,
    isPopoverOpen,
    showPopover,
    hidePopover,
    onSelectHandler,
  }
}
